import { useEffect, useState } from 'react';
import { localApi } from '../utils/localApi';
import { Shield, Download, RefreshCw, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface SecurityEvent {
    id: string;
    created_at: string;
    event_type: string;
    driver_name: string;
    train_number: string;
    pk: number;
    coords: string;
    payload: any;
    device_timestamp: string;
}

export default function SecurityRegisterView() {
    const [events, setEvents] = useState<SecurityEvent[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const data = await localApi.getBlackboxEvents();
            setEvents(data);
        } catch (err) {
            console.error("Erreur chargement blackbox:", err);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const handleExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Registre de Sécurité');

        worksheet.columns = [
            { header: 'Date/Heure Serveur', key: 'created_at', width: 25 },
            { header: 'Événement', key: 'event_type', width: 25 },
            { header: 'Conducteur', key: 'driver_name', width: 20 },
            { header: 'Train n°', key: 'train_number', width: 15 },
            { header: 'PK', key: 'pk', width: 10 },
            { header: 'Coordonnées', key: 'coords', width: 20 },
            { header: 'Détails', key: 'details', width: 50 },
            { header: 'Horodatage Tablette', key: 'device_timestamp', width: 25 },
            { header: 'ID Unique', key: 'id', width: 40 }
        ];

        events.forEach(e => {
            worksheet.addRow({
                created_at: new Date(e.created_at).toLocaleString('fr-FR'),
                event_type: e.event_type,
                driver_name: e.driver_name,
                train_number: e.train_number,
                pk: e.pk ? e.pk.toFixed(3) : '0.000',
                coords: e.coords,
                details: JSON.stringify(e.payload),
                device_timestamp: new Date(e.device_timestamp).toLocaleString('fr-FR'),
                id: e.id
            });
        });

        worksheet.getRow(1).font = { bold: true };

        const buffer = await workbook.xlsx.writeBuffer();
        const data = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(data, `BlackBox_NexusRail_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const getEventIcon = (type: string) => {
        if (type === 'INCIDENT_REPORT') return <AlertTriangle className="text-red-500 w-4 h-4" />;
        if (type === 'INCIDENT_RESOLVED') return <CheckCircle className="text-emerald-500 w-4 h-4" />;
        return <Info className="text-blue-500 w-4 h-4" />;
    };

    return (
        <div className="flex flex-col h-full bg-[#0f172a] text-white">
            <div className="p-6 border-b border-white/10 flex justify-between items-center bg-slate-900/50">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                        <Shield className="text-red-500 w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-xl font-black uppercase tracking-tighter">Registre Sécurité (Boîte Noire)</h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Journal officiel et infalsifiable des événements</p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={fetchEvents}
                        disabled={loading}
                        className="p-3 bg-slate-800 hover:bg-slate-700 rounded-xl transition-all border border-white/5 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-3 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] transition-all shadow-lg shadow-emerald-900/20 active:scale-95 border border-emerald-400/20"
                    >
                        <Download size={16} />
                        Exporter .xlsx
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                <div className="glass-panel-heavy rounded-3xl overflow-hidden border border-white/5">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-white/5 border-b border-white/10">
                                <th className="px-4 py-4 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500">Horodatage (SRV)</th>
                                <th className="px-4 py-4 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500">Événement</th>
                                <th className="px-4 py-4 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500">Agent</th>
                                <th className="px-4 py-4 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500">Train</th>
                                <th className="px-4 py-4 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500">Position</th>
                                <th className="px-4 py-4 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-500">Détails</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {events.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center text-slate-600 text-[10px] font-bold uppercase tracking-widest italic">
                                        Aucun événement enregistré dans le registre.
                                    </td>
                                </tr>
                            ) : (
                                events.map((event) => (
                                    <tr key={event.id} className="hover:bg-white/5 transition-colors group">
                                        <td className="px-4 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-white font-mono text-[11px] font-bold">
                                                    {new Date(event.created_at).toLocaleTimeString('fr-FR')}
                                                </span>
                                                <span className="text-slate-500 text-[9px] font-bold">
                                                    {new Date(event.created_at).toLocaleDateString('fr-FR')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-2">
                                                {getEventIcon(event.event_type)}
                                                <span className={`text-[10px] font-black tracking-widest uppercase ${event.event_type === 'INCIDENT_REPORT' ? 'text-red-400' :
                                                    event.event_type === 'INCIDENT_RESOLVED' ? 'text-emerald-400' :
                                                        'text-blue-400'
                                                    }`}>
                                                    {event.event_type.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="text-white text-[11px] font-black italic">{event.driver_name}</span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="px-2 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded text-[9px] font-black">
                                                TR {event.train_number}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-white font-mono text-[10px] font-bold">PK {event.pk ? event.pk.toFixed(3) : '0.000'}</span>
                                                <span className="text-slate-500 text-[8px] font-mono truncate max-w-[100px]">{event.coords}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <p className="text-slate-400 text-[10px] line-clamp-2 italic leading-relaxed">
                                                {event.event_type === 'INCIDENT_REPORT' ? event.payload.description :
                                                    event.event_type === 'INCIDENT_RESOLVED' ? `Incident ${event.payload.incident.type} marqué comme ${event.payload.status === 'ABSENT' ? 'ZONE LIBÉRÉE' : 'TOUJOURS PRÉSENT'}` :
                                                        JSON.stringify(event.payload)}
                                            </p>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="p-4 px-6 border-t border-white/5 bg-black/40 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                        <span className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em]">Black Box Active</span>
                    </div>
                </div>
                <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest italic">
                    Conformité Réglementaire CFC • Nexus Rail v2.5.0
                </p>
            </div>
        </div>
    );
}
